/**
 * Codegen for "Enable push notifications".
 *
 * Provisioning a OneSignal app only gets credentials onto the server. For the
 * built app to actually register devices, the packet's own source has to
 * initialize the SDK — so enabling push writes code into the app:
 *
 *   1. `package.json`                        + SDK, native module, expo plugin
 *   2. `src/config/constants.ts`             + ONESIGNAL_APP_ID (see
 *                                             `planAppIdPlacement` — apps with no
 *                                             `@/config` get it in the provider)
 *   3. `src/core/providers/TileNotificationProvider.tsx`  (new file)
 *   4. `src/core/providers/index.ts`         + barrel export
 *   5. `app.json`                            + the onesignal-expo-plugin entry
 *   6. `App.js`                              import + wrap the app in it
 *
 * Steps 1 and 5 are what make a REAL build work; 2–4 and 6 are what make it run.
 * Miss step 5 and the app installs fine and never receives a notification,
 * because prebuild leaves out the iOS push entitlement.
 *
 * ## Why a provider, not a call in App.js
 *
 * Two reasons. The blueprint already composes behaviour as providers
 * (`ThemeProvider`, `AnalyticsProvider`, `LiveLayerProvider`…), so this matches
 * the shape a developer expects to find. And the editor's AST toolkit has
 * `ensureImport` but no statement-insertion transform, so inserting a call into
 * `App()`'s body would need a new mutator written first, while wrapping a JSX
 * element is a small, contained transform.
 *
 * ## Why AST and not string surgery
 *
 * `IntegrationsPanel` does the equivalent job with regex —
 * `result.replace(/return\s*\(\s*\n(\s*)/, …)` plus `lastIndexOf(');')` — which
 * silently mangles any file whose formatting differs from the blueprint's. The
 * JSX wrap here goes through recast so formatting elsewhere in the file is
 * preserved and a non-matching file is reported instead of corrupted.
 *
 * Every step is idempotent: enabling twice is a no-op, and a partially-wired app
 * (say the provider file was deleted) is repaired rather than duplicated.
 */
import {
  jsxClosingElement,
  jsxElement,
  jsxIdentifier,
  jsxOpeningElement,
  jsxText,
} from '@babel/types';
import { parse, print, types } from 'recast';

import { missingPackages } from '@/ast/readers/dependencies/missing-packages';
import { createParser } from '@/ast/readers/properties/parse';
import { ensureImport, FileContents } from '@/ast/writers/properties/edit';

/** Published SDK that owns init, tagging and identity. */
export const NOTIFICATION_PACKAGE = '@tiledev/sdk-tile-notification';
export const NOTIFICATION_PACKAGE_VERSION = '^0.1.0';

/**
 * Everything a standalone build needs installed.
 *
 * `react-native-onesignal` is an OPTIONAL peer of the SDK — deliberately, so the
 * web bundle never pulls it — which means npm will not install it on its own. It
 * has to be a direct dependency of the app or the build ships the JS wrapper with
 * no native module behind it, and the SDK's lazy require throws on first launch.
 *
 * `onesignal-expo-plugin` is what makes `expo prebuild` add the iOS push
 * entitlement, the App Group and the Notification Service Extension. Versions
 * match tile-run-engine's own OneSignal setup so both planes stay on one major.
 */
const EXPO_PLUGIN = 'onesignal-expo-plugin';
const NATIVE_MODULE = 'react-native-onesignal';
const APP_CONFIG_PATH = 'app.json';

const REQUIRED_DEPENDENCIES: Record<string, string> = {
  [NOTIFICATION_PACKAGE]: NOTIFICATION_PACKAGE_VERSION,
  [NATIVE_MODULE]: '^5.5.3',
  [EXPO_PLUGIN]: '^2.7.0',
};

/**
 * The one dependency the preview bundler must never be asked to build.
 *
 * Verified against the running bundler rather than assumed, because guessing here
 * is what broke the first attempt:
 *
 *   @tiledev/sdk-tile-notification  → 200, bundles clean
 *   react-native-onesignal@5.5.3    → 200, bundles clean (only needs react-native)
 *   onesignal-expo-plugin@2.7.0     → error: Cannot resolve module fs/promises
 *
 * So only the plugin is excluded — it is build-time Node code and was never
 * bundleable. The other two stay fetchable, for two separate reasons:
 *
 *   - The SDK: excluding a module the run-engine doesn't alias makes it resolve to
 *     the null-returning stub in Modules.tsx, which is a crash, not a no-op. Its
 *     web build is plain JS, so the preview runs the real published no-op.
 *   - The native module: the SDK declares it as a peer, and getMissingDependencies
 *     (tile-packet-sdk/src/DependencyResolver.ts:166) flags a peer only when it is
 *     absent from `dependencies`. Dropping it from the fetch list would flag it
 *     missing at a version that does not exist — the bundler reads the peer range
 *     `>=5.0.0 <6.0.0` and reports `6.0.0`.
 */
export const NOTIFICATION_UNBUNDLEABLE = [EXPO_PLUGIN];

/**
 * Imported by the generated provider. Both ship in the blueprint, so this only
 * bites on an imported repo — warn rather than add them, because pinning a
 * version for a dependency the app already manages is how you get a duplicate
 * React Native module at build time.
 */
const EXPECTED_PEERS = ['expo-linking', '@react-native-async-storage/async-storage'];

/**
 * Stamped into the generated provider so a later version of this codegen can tell
 * "this is still exactly what we wrote" from "the developer has edited it".
 *
 * Bump the number whenever `providerSource()` changes in a way existing apps need,
 * and keep every older marker prefix in `LEGACY_MARKER_PREFIXES` so those apps are
 * still recognised as ours and get upgraded instead of left behind.
 *
 * v1 chained `.init(...).catch(...)`, which crashes in the editor preview: an
 * unresolved module there is a Proxy stub whose calls return `null`, so the
 * `.catch` threw `Cannot read properties of null`. v2 awaits inside a try.
 * v3 renamed the provider from OneSignalProvider to TileNotificationProvider.
 * v4 stopped importing `@/config` unconditionally — see `planAppIdPlacement`.
 */
const GENERATED_MARKER = '@generated tile-notification-provider v4';
const GENERATED_MARKER_PREFIX = '@generated tile-notification-provider v';
const LEGACY_MARKER_PREFIXES = ['@generated onesignal-provider v'];

/**
 * Named for what it does in the app, not for the vendor behind it. OneSignal is
 * an implementation detail of the notification center — swapping it later
 * shouldn't mean a breaking rename in every app that turned push on.
 */
const PROVIDER_NAME = 'TileNotificationProvider';
/** Exported so the Notification Center can point the user straight at the file. */
export const PROVIDER_PATH = 'src/core/providers/TileNotificationProvider.tsx';
const PROVIDERS_BARREL = 'src/core/providers/index.ts';
const CONSTANTS_PATH = 'src/config/constants.ts';

/**
 * Every file `@/config` can resolve to, in the run engine's own priority order
 * (`Modules.tsx` System.resolve: the bare path with each extension, then
 * `<dir>/index` with each extension).
 *
 * The generated provider used to import `@/config` unconditionally, on the
 * assumption that the blueprint's `src/config/` is always there. On an app
 * without it that import is unresolvable and takes the whole app down —
 * `Unable to resolve module 'src/config.js'` before anything renders. Enabling
 * push must never be able to break an app, so the import is only emitted when
 * one of these files proves it will resolve.
 */
const CONFIG_MODULE_CANDIDATES = [
  'src/config.tsx',
  'src/config.ts',
  'src/config.js',
  'src/config.jsx',
  'src/config/index.tsx',
  'src/config/index.ts',
  'src/config/index.js',
  'src/config/index.jsx',
];
const APP_ENTRY_CANDIDATES = [
  'App.js',
  'App.tsx',
  'App.ts',
  'App.jsx',
  'src/App.tsx',
  'src/App.js',
  'src/App.jsx',
  'index.js',
  'index.tsx',
  // expo-router projects have no App component; the root layout is the entry.
  'app/_layout.tsx',
  'src/app/_layout.tsx',
];

export type FileUpdate = { type: string; contents: string };
export type FileUpdates = { [path: string]: FileUpdate | null };

export interface CodegenResult {
  updates: FileUpdates;
  /** Non-fatal problems. Enabling still succeeds; surface these to the user. */
  warnings: string[];
}

/* ─── helpers ──────────────────────────────────────────────────────── */

function readFile(files: FileContents, path: string): string | null {
  const f = files[path];
  if (!f || typeof (f as any).contents !== 'string') return null;
  return (f as any).contents as string;
}

function findAppEntry(files: FileContents): string | null {
  return APP_ENTRY_CANDIDATES.find((p) => readFile(files, p) !== null) ?? null;
}

/**
 * Parse a JSON file, or null if it doesn't parse.
 *
 * The JSON steps take an already-parsed object so that `null` from them means
 * exactly "nothing to change". Folding a parse failure into the same `null` would
 * make a broken package.json look like an app that was already wired — i.e. push
 * silently never working, with no warning.
 */
function parseJson(contents: string): any | null {
  try {
    const parsed = JSON.parse(contents);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/* ─── 1. dependencies ──────────────────────────────────────────────── */

/** The SDK packages the app's package.json lacks — what enabling would add, after asking. */
export function oneSignalMissingPackages(files: FileContents): string[] {
  return missingPackages(readFile(files, 'package.json'), Object.keys(REQUIRED_DEPENDENCIES));
}

/** Add the missing ones at our pins; versions the app already has are left alone. */
function addDependencies(pkg: any, missing: string[]): string {
  const deps = { ...(pkg.dependencies ?? {}) };
  for (const name of missing) deps[name] = REQUIRED_DEPENDENCIES[name];
  // Keep dependencies sorted — npm rewrites them sorted anyway, and an
  // unsorted insert shows up as a spurious diff on the next install.
  pkg.dependencies = Object.keys(deps)
    .sort()
    .reduce((acc: Record<string, string>, k) => {
      acc[k] = deps[k];
      return acc;
    }, {});
  return JSON.stringify(pkg, null, 2) + '\n';
}

/* ─── 2. app id constant ───────────────────────────────────────────── */

/**
 * The OneSignal App ID is public (it ships inside every client binary), so
 * writing it into config is safe — unlike the REST key, which never leaves
 * tile-backend. Kept overridable by EXPO_PUBLIC_ONESIGNAL_APP_ID so a build can
 * point at a different OneSignal app per environment, matching how
 * LIVE_LAYER_APP_ID is declared right above it.
 */
function upsertAppIdConstant(contents: string, onesignalAppId: string): string | null {
  const line =
    '\n// Push notifications (OneSignal). The App ID is public; the REST API key\n' +
    '// lives server-side and is never shipped in the app.\n' +
    'export const ONESIGNAL_APP_ID =\n' +
    `  process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID || "${onesignalAppId}";\n`;

  const existing = /export const ONESIGNAL_APP_ID\s*=[\s\S]*?;\n/;
  if (existing.test(contents)) {
    const replaced = contents.replace(
      existing,
      `export const ONESIGNAL_APP_ID =\n  process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID || "${onesignalAppId}";\n`,
    );
    return replaced === contents ? null : replaced;
  }
  return contents.trimEnd() + '\n' + line;
}

/**
 * Where `ONESIGNAL_APP_ID` should live in THIS app.
 *
 *   { kind: 'config' } — `@/config` provably resolves, so the constant goes in a
 *     config file and the provider imports it. `path` is the file to write.
 *   { kind: 'inline' } — this app has no `@/config`. The provider declares the
 *     constant itself: one self-contained file, nothing to resolve, no crash.
 */
type AppIdPlacement = { kind: 'config'; path: string } | { kind: 'inline' };

/** The file `@/config` resolves to in this app, or null if there is none. */
function findConfigModule(files: FileContents): string | null {
  return CONFIG_MODULE_CANDIDATES.find((p) => readFile(files, p) !== null) ?? null;
}

const CONFIG_IMPORT_RE = /import\s[^;]*from\s+["']@\/config["']/;

function planAppIdPlacement(files: FileContents): AppIdPlacement {
  const configModule = findConfigModule(files);
  if (!configModule) return { kind: 'inline' };

  // Blueprint shape: `src/config/index.ts` is a barrel over `./constants`. Keep
  // that split — write the constant beside the app's other constants rather than
  // into a file that only re-exports. Only when the barrel actually re-exports
  // constants, though; otherwise the import would resolve to a module that
  // doesn't export ONESIGNAL_APP_ID, and push would silently never initialize.
  if (/^src\/config\/index\./.test(configModule)) {
    const barrel = readFile(files, configModule) ?? '';
    if (readFile(files, CONSTANTS_PATH) !== null && /['"]\.\/constants['"]/.test(barrel)) {
      return { kind: 'config', path: CONSTANTS_PATH };
    }
  }
  return { kind: 'config', path: configModule };
}

/* ─── 3. provider file ─────────────────────────────────────────────── */

function providerSource(placement: AppIdPlacement, onesignalAppId: string): string {
  // Import it when `@/config` resolves; declare it here when it doesn't. An
  // import of a module this app hasn't got is a hard crash at startup, not a
  // degraded feature.
  const appId =
    placement.kind === 'config'
      ? `import { ONESIGNAL_APP_ID } from "@/config";`
      : `// This app has no \`@/config\` module, so the App ID lives here instead.
// It is public (it ships inside every client binary); the REST API key stays
// server-side and is never shipped. Override per environment with
// EXPO_PUBLIC_ONESIGNAL_APP_ID.
const ONESIGNAL_APP_ID =
  process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID || "${onesignalAppId}";`;

  return `import React, { useEffect } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Linking from "expo-linking";
import { onesignal } from "${NOTIFICATION_PACKAGE}";

${appId}

/**
 * Registers this device for push so the Notification Center can reach it, and
 * forwards commerce events as user tags so audience targeting works. Delivery runs
 * on OneSignal today; that is an implementation detail of this provider.
 *
 * Safe to edit. Re-enabling push replaces this file only while it is still
 * recognisably the Notification Center's own output (see the marker below); once
 * you have edited it, it is left alone.
 *
 * On web every call is a no-op: the underlying native module has no web build.
 *
 * ${GENERATED_MARKER}
 */
export function ${PROVIDER_NAME}({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!ONESIGNAL_APP_ID) return;
    // Deliberately await-inside-try rather than \`.init(...).catch(...)\`: in the
    // editor preview an unresolved module is a stub whose calls return null, and
    // a promise chain on null throws for real. \`await null\` is fine, and this
    // also catches a synchronous throw. Never let push break app startup.
    (async () => {
      try {
        await onesignal.init({
          appId: ONESIGNAL_APP_ID,
          // Persists the "user declined" timestamp so we don't re-prompt on
          // every launch.
          storage: AsyncStorage,
          reOptInPeriodDays: 7,
          onDeeplink: (url) => {
            Promise.resolve(Linking.openURL(url)).catch(() => {});
          },
        });
      } catch (error) {
        console.warn("[TileNotification] init failed", error);
      }
    })();
  }, []);

  return <>{children}</>;
}

/**
 * Call these from your commerce flows — OneSignal segments are built ONLY from
 * tags and outcomes, so the Notification Center's automations (Abandoned Cart,
 * Order Success, New User Welcome) stay empty until these fire.
 *
 *   onesignal.trackEvent("updateCartQuantity", { totalQuantity: 2 });
 *   onesignal.trackEvent("purchase", { totalValue: 49.99 });
 *   onesignal.identify({ customerId, email });
 */
export { onesignal } from "${NOTIFICATION_PACKAGE}";
`;
}

/**
 * True when this file is an OLDER version of what `providerSource()` emits, and
 * so safe to replace. Two ways to recognise that:
 *
 *   - it carries one of our markers, current or historical, with a different
 *     version number, or
 *   - it has v1's `.init({…}).catch(…)` chain, which predates the marker. That
 *     chain is the crash itself, so leaving it in place is not an option: any app
 *     enabled before v2 has a provider that throws in the editor preview.
 *
 * Anything else — no marker and no v1 signature — is treated as developer-owned
 * and left untouched, which is why this checks for a specific shape rather than
 * just "did we write the header comment".
 */
function isGeneratedProvider(contents: string): boolean {
  if (contents.includes(GENERATED_MARKER_PREFIX)) return true;
  if (LEGACY_MARKER_PREFIXES.some((prefix) => contents.includes(prefix))) return true;
  const v1Chain = /onesignal\s*\n?\s*\.init\(\{[\s\S]*?\}\)\s*\n?\s*\.catch\(/;
  return v1Chain.test(contents);
}

/** Ours, but not the current version — safe to replace with a fresh emit. */
function isStaleGeneratedProvider(contents: string): boolean {
  if (contents.includes(GENERATED_MARKER)) return false;
  return isGeneratedProvider(contents);
}

/**
 * Ours, current version, and importing a config module this app hasn't got.
 *
 * Reachable when the app's config module is deleted after push was enabled — or,
 * for one release, when the provider assumed `@/config` existed. Either way the
 * import is unresolvable and the app won't start, so this counts as needing a
 * regenerate even though the marker is current. A provider the developer owns is
 * left alone: clobbering their file is worse than the broken import.
 */
function hasUnresolvableConfigImport(contents: string, placement: AppIdPlacement): boolean {
  return (
    placement.kind === 'inline' && isGeneratedProvider(contents) && CONFIG_IMPORT_RE.test(contents)
  );
}

/* ─── 4. barrel ────────────────────────────────────────────────────── */

function addBarrelExport(contents: string): string | null {
  if (contents.includes(`./${PROVIDER_NAME}`)) return null;
  const line = `export { ${PROVIDER_NAME} } from "./${PROVIDER_NAME}";\n`;
  return contents.trimEnd() + '\n' + line;
}

/* ─── 5. app.json config plugin ────────────────────────────────────── */

/**
 * Register `onesignal-expo-plugin` so `expo prebuild` wires the native side.
 *
 * Added per-app at enable time rather than baked into the blueprint: the plugin
 * adds an iOS push entitlement and an extra target, so every app that has it
 * needs a provisioning profile with push enabled. Apps that never turn on push
 * shouldn't inherit that.
 *
 * `mode: "development"` matches tile-run-engine's own app.json. It is the
 * tolerant setting — a development APS entitlement is accepted by dev, ad-hoc and
 * store profiles, whereas "production" fails on development builds. app.json is
 * static JSON so it can't carry a comment saying so; hence this one.
 */
function addExpoPlugin(cfg: any): string | null {
  // An app.json with no `expo` key isn't an Expo config; adding one would be a
  // guess. The caller warns instead.
  if (!cfg.expo || typeof cfg.expo !== 'object') return null;
  const plugins: unknown[] = Array.isArray(cfg.expo.plugins) ? cfg.expo.plugins : [];

  // Present either as a bare string or as [name, options].
  const already = plugins.some((p) =>
    typeof p === 'string' ? p === EXPO_PLUGIN : Array.isArray(p) && p[0] === EXPO_PLUGIN,
  );
  if (already) return null;

  cfg.expo.plugins = [...plugins, [EXPO_PLUGIN, { mode: 'development' }]];
  return JSON.stringify(cfg, null, 2) + '\n';
}

/* ─── 6. App entry: import + JSX wrap ──────────────────────────────── */

/** Wrap a JSX node in `<wrapperName>…</wrapperName>`, preserving indentation. */
function wrapNode(node: any, wrapperName: string): any {
  return jsxElement(
    jsxOpeningElement(jsxIdentifier(wrapperName), []),
    jsxClosingElement(jsxIdentifier(wrapperName)),
    [jsxText('\n'), node, jsxText('\n')],
    false,
  );
}

/**
 * Wrap whatever the app's root component renders in `<wrapperName>`.
 *
 * Deliberately NOT keyed on element names. An earlier version looked for
 * `<Navigation>` / `<Navigator>` / `<RootNavigator>` because that is what the
 * blueprint happens to use, and it failed on any app with a different root —
 * which is most of them. The provider needs no context from its children (it
 * only runs an effect and renders `children`), so wrapping the outermost
 * returned element is both safe and shape-independent.
 *
 * Handles every component form in practice:
 *   export default function App() { return (<X/>); }
 *   export default function App() { return <X/>; }
 *   const App = () => (<X/>);            export default App;
 *   const App = () => { return <X/>; }   export default App;
 *   export default () => <X/>;
 *   fragment roots, and multiple conditional returns (all are wrapped, so the
 *   provider mounts whichever branch renders).
 *
 * Returns null only when the file has no JSX to wrap at all.
 */
export function wrapAppRoot(contents: string, fileKey: string, wrapperName: string): string | null {
  // Cheap idempotency guard: already wrapped somewhere → nothing to do.
  if (contents.includes(`<${wrapperName}`)) return null;

  try {
    const ast = parse(contents, { parser: createParser(fileKey) });
    let count = 0;

    const isJsx = (n: any) => n && (n.type === 'JSXElement' || n.type === 'JSXFragment');

    /** True when no ancestor of this path is itself JSX — i.e. a render root. */
    const isRootJsx = (path: any): boolean => {
      let p = path.parentPath;
      while (p) {
        if (isJsx(p.node)) return false;
        p = p.parentPath;
      }
      return true;
    };

    // Wrap every ROOT JSX node in the file. For an app entry these are exactly
    // the things the component(s) render — nested JSX is skipped by isRootJsx,
    // and a file with several conditional returns gets all of them wrapped so
    // the provider mounts on any branch.
    types.visit(ast, {
      visitJSXElement(path: any) {
        if (isRootJsx(path)) {
          path.replace(wrapNode(path.node, wrapperName));
          count++;
          return false; // don't descend into what we just wrapped
        }
        this.traverse(path);
        return undefined;
      },
      visitJSXFragment(path: any) {
        if (isRootJsx(path)) {
          path.replace(wrapNode(path.node, wrapperName));
          count++;
          return false;
        }
        this.traverse(path);
        return undefined;
      },
    });

    if (count === 0) return null;
    return print(ast).code;
  } catch {
    return null;
  }
}

/* ─── public API ───────────────────────────────────────────────────── */

/**
 * True when the app is wired end to end — provider file, barrel export, the
 * native dependency and the expo plugin.
 *
 * The last two matter as much as the first: an app with the provider but without
 * `onesignal-expo-plugin` builds and installs and simply never receives a push,
 * which is exactly the failure this whole flow exists to remove. Reporting it as
 * wired would hide that, so the Notification Center's "Add SDK" repair stays
 * available until all four are present.
 */
export function isOneSignalWired(files: FileContents): boolean {
  const provider = readFile(files, PROVIDER_PATH);
  const barrel = readFile(files, PROVIDERS_BARREL);
  if (!provider || !barrel || !barrel.includes(PROVIDER_NAME)) return false;

  // A provider importing `@/config` in an app that has no config module is worse
  // than not wired: the import is unresolvable, so the app dies at startup
  // instead of just missing push. Report it as unwired so the repair button
  // appears and one click regenerates a self-contained provider.
  if (hasUnresolvableConfigImport(provider, planAppIdPlacement(files))) return false;

  const pkgRaw = readFile(files, 'package.json');
  const pkg = pkgRaw === null ? null : parseJson(pkgRaw);
  const deps = pkg?.dependencies ?? {};
  if (Object.keys(REQUIRED_DEPENDENCIES).some((name) => !deps[name])) return false;

  // A missing app.json can't be repaired by codegen, so don't let it peg this to
  // false forever — only a readable Expo config lacking the plugin counts.
  const cfgRaw = readFile(files, APP_CONFIG_PATH);
  const cfg = cfgRaw === null ? null : parseJson(cfgRaw);
  if (cfg?.expo) {
    const plugins: unknown[] = Array.isArray(cfg.expo.plugins) ? cfg.expo.plugins : [];
    const hasPlugin = plugins.some((p) =>
      typeof p === 'string' ? p === EXPO_PLUGIN : Array.isArray(p) && p[0] === EXPO_PLUGIN,
    );
    if (!hasPlugin) return false;
  }
  return true;
}

/**
 * Build the file updates that wire the notification SDK into this app.
 *
 * Pure — returns updates for the caller to hand to `updateFiles`, so it can be
 * tested without an editor session. Each step is skipped when already done, so
 * the result is empty for an app that is fully wired.
 */
export function applyOneSignalCodegen(
  files: FileContents,
  /** `addPackages`: the user accepted adding the SDK packages the app lacks. */
  opts: { onesignalAppId: string; addPackages?: boolean },
): CodegenResult {
  const updates: FileUpdates = {};
  const warnings: string[] = [];

  // 1. dependencies — added only when the user accepted (the same card a tile drop shows): a
  // native one blocks every later code push until a new build. Without them the provider can't
  // import its SDK, so nothing is written at all.
  const pkgRaw = readFile(files, 'package.json');
  const pkg = pkgRaw === null ? null : parseJson(pkgRaw);
  const missing = oneSignalMissingPackages(files);
  if (missing.length && (!opts.addPackages || pkg === null)) {
    // eslint-disable-next-line no-console
    console.warn(`[Notifications] Not wired: the app lacks ${missing.join(', ')}.`);
    return { updates: {}, warnings: ["Push notifications weren't turned on for this app."] };
  }
  if (pkg) {
    if (missing.length) {
      updates['package.json'] = { type: 'CODE', contents: addDependencies(pkg, missing) };
    }
    const deps = { ...(pkg.dependencies ?? {}), ...(pkg.devDependencies ?? {}) };
    const absent = EXPECTED_PEERS.filter((name) => !deps[name]);
    if (absent.length) {
      warnings.push(
        `The generated provider imports ${absent.join(' and ')}, which this app ` +
          `doesn't depend on — add it, or edit ${PROVIDER_NAME}.tsx to drop the import.`,
      );
    }
  }

  // 2. app id constant — only into a config module the provider can actually
  // import. Where there is none, the provider carries the constant itself
  // (step 3), so there is nothing to write and nothing to warn about.
  const placement = planAppIdPlacement(files);
  if (placement.kind === 'config') {
    const next = upsertAppIdConstant(readFile(files, placement.path)!, opts.onesignalAppId);
    if (next) updates[placement.path] = { type: 'CODE', contents: next };
  }

  // 3. provider file — create it, or upgrade an older one we generated
  const existingProvider = readFile(files, PROVIDER_PATH);
  if (
    existingProvider === null ||
    isStaleGeneratedProvider(existingProvider) ||
    hasUnresolvableConfigImport(existingProvider, placement)
  ) {
    updates[PROVIDER_PATH] = {
      type: 'CODE',
      contents: providerSource(placement, opts.onesignalAppId),
    };
  }

  // 4. barrel
  const barrel = readFile(files, PROVIDERS_BARREL);
  if (barrel === null) {
    updates[PROVIDERS_BARREL] = {
      type: 'CODE',
      contents: `export { ${PROVIDER_NAME} } from "./${PROVIDER_NAME}";\n`,
    };
  } else {
    const next = addBarrelExport(barrel);
    if (next) updates[PROVIDERS_BARREL] = { type: 'CODE', contents: next };
  }

  // 5. expo config plugin — without it prebuild produces a build with no push
  // entitlement, so the app installs but never receives a notification.
  const appConfigRaw = readFile(files, APP_CONFIG_PATH);
  const appConfig = appConfigRaw === null ? null : parseJson(appConfigRaw);
  if (appConfig === null || !appConfig.expo) {
    warnings.push(
      `Couldn't read an Expo config from ${APP_CONFIG_PATH} — add "${EXPO_PLUGIN}" to its ` +
        'plugins manually, or iOS builds will have no push entitlement.',
    );
  } else {
    const next = addExpoPlugin(appConfig);
    if (next) updates[APP_CONFIG_PATH] = { type: 'CODE', contents: next };
  }

  // 6. app entry — import, then wrap
  const entryPath = findAppEntry(files);
  if (!entryPath) {
    warnings.push(
      `Could not find the app entry (looked for ${APP_ENTRY_CANDIDATES.join(', ')}). ` +
        `Wrap your app in <${PROVIDER_NAME}> manually to finish setup.`,
    );
  } else {
    // ensureImport works on the file map, so run it against a map that already
    // carries the edits above — otherwise its result would drop them.
    const merged: FileContents = { ...files };
    for (const [path, upd] of Object.entries(updates)) {
      if (upd) merged[path] = { type: 'CODE', contents: upd.contents } as any;
    }

    const imported = ensureImport(merged, entryPath, '@/core/providers', PROVIDER_NAME);
    const afterImport = imported.success
      ? readFile(imported.updatedFiles, entryPath) ?? readFile(merged, entryPath)!
      : readFile(merged, entryPath)!;
    if (!imported.success) {
      warnings.push(
        `Could not add the ${PROVIDER_NAME} import to ${entryPath}: ${
          imported.error ?? 'unknown error'
        }.`,
      );
    }

    const jsxWrapped = wrapAppRoot(afterImport, entryPath, PROVIDER_NAME);
    if (jsxWrapped) {
      updates[entryPath] = { type: 'CODE', contents: jsxWrapped };
    } else if (!afterImport.includes(`<${PROVIDER_NAME}`)) {
      // Only reachable when the entry renders no JSX at all — a re-export shim,
      // or an entry that just calls registerRootComponent.
      warnings.push(
        `${entryPath} doesn't render any JSX, so the provider couldn't be added there. ` +
          `Wrap your root component in <${PROVIDER_NAME}> to finish setup.`,
      );
      // Still persist the import so the manual step is one edit, not two.
      if (afterImport !== readFile(files, entryPath)) {
        updates[entryPath] = { type: 'CODE', contents: afterImport };
      }
    }
  }

  return { updates, warnings };
}
